// An area's icon (docs/61; AreaIconPicker on the iPhone, MacAreaIconPopover on the Mac): the emoji leading its title,
// shown in a soft tile on the area's page. A click opens a popover with a search over every emoji (English and German
// names, loaded when it opens), the suggestions for the area's name and the often used ones. Picking writes
// "emoji + space + name" back as the title, one step of Undo; the name stays. Only the area's owner changes it. An area
// without an icon (made by an older version) shows a grey dashed square in its place.

import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type RefObject } from "react";
import { areaIconSuggestions, joinEmoji, OFTEN_USED_AREA_EMOJI, splitEmoji } from "../core/areaIcons";
import type { Area } from "../core/records";
import { currentLanguage, t } from "../i18n";
import { useWindow } from "./context";
import { emojiLabels, loadEmoji, searchEmoji, type EmojiEntry } from "./emojiSearch";
import { Icon } from "./Icon";
import { Popover } from "./Menu";

/** The grey dashed square in the place of a missing icon. */
export function NoIconMark({ size }: { size: number }) {
  return (
    <svg className="no-icon" viewBox="0 0 20 20" width={size} height={size} aria-hidden="true" focusable="false">
      <rect x="2" y="2" width="16" height="16" rx="4.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 2.4" />
    </svg>
  );
}

/** Whether this person may change the area's icon: their own area, or a shared one they own. */
export function useOwnsArea(area: Area | undefined): boolean {
  const { model } = useWindow();
  if (!area) return false;
  const share = model.shareOf({ kind: "area", id: area.id });
  return share === null || share.youOwn;
}

/** The icon on the area's page: a button opening the popover for the owner, the emoji alone for the others. */
export function AreaIconButton({ area }: { area: Area }) {
  const owns = useOwnsArea(area);
  const button = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const { emoji } = splitEmoji(area.title);
  const face = emoji ?? <NoIconMark size={26} />;
  if (!owns) return emoji ? <span className="area-icon-tile">{emoji}</span> : null;
  return (
    <>
      <button ref={button} className="area-icon-tile" aria-label={emoji ? t("Change Icon") : t("Choose Icon")} title={emoji ? t("Change Icon") : t("Choose Icon")}
        aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
        {face}
      </button>
      {open && <AreaIconPopover anchor={button} area={area} onClose={close} />}
    </>
  );
}

/** The popover: search, the suggestions for the name, the often used ones. */
export function AreaIconPopover({ anchor, area, onClose }: { anchor: RefObject<HTMLElement | null>; area: Area; onClose: () => void }) {
  const { model } = useWindow();
  const { emoji: current, name } = splitEmoji(area.title);
  const [query, setQuery] = useState("");
  const [entries, setEntries] = useState<EmojiEntry[]>();
  const field = useRef<HTMLInputElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    field.current?.focus({ preventScroll: true });
    let live = true;
    loadEmoji().then((loaded) => { if (live) setEntries(loaded); }, () => undefined);
    return () => { live = false; };
  }, []);

  const searching = query.trim() !== "";
  const suggested = useMemo(() => areaIconSuggestions(name), [name]);
  const results = useMemo(() => (entries && searching ? searchEmoji(entries, query) : []), [entries, searching, query]);
  const labels = useMemo(() => (entries ? emojiLabels(entries, currentLanguage()) : new Map<string, string>()), [entries]);

  const choose = (emoji: string) => {
    onClose();
    if (emoji !== current) model.updateArea({ ...area, title: joinEmoji(emoji, name) });
  };

  // ← → move along a grid, ↑ ↓ a row of eight, across the grids; ↑ from the first row goes back to the search field.
  const move = (event: KeyboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    const grids = [...(panel.current?.querySelectorAll<HTMLElement>(".emoji-grid") ?? [])];
    const grid = target.closest<HTMLElement>(".emoji-grid");
    if (!grid || !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
    event.preventDefault();
    const tiles = (of: Element | undefined) => [...(of?.querySelectorAll<HTMLButtonElement>(".emoji-tile") ?? [])];
    const own = tiles(grid);
    const index = own.indexOf(target as HTMLButtonElement);
    const column = index % 8;
    const gridIndex = grids.indexOf(grid);
    let next: HTMLElement | undefined;
    if (event.key === "ArrowLeft") next = own[index - 1] ?? tiles(grids[gridIndex - 1]).at(-1);
    else if (event.key === "ArrowRight") next = own[index + 1] ?? tiles(grids[gridIndex + 1])[0];
    else if (event.key === "ArrowDown") {
      const below = tiles(grids[gridIndex + 1]);
      next = own[index + 8] ?? (index + 8 < Math.ceil(own.length / 8) * 8 ? own.at(-1) : below[Math.min(column, below.length - 1)]);
    } else {
      if (index >= 8) next = own[index - 8];
      else {
        const above = tiles(grids[gridIndex - 1]);
        const lastRow = Math.floor((above.length - 1) / 8) * 8;
        next = above.length > 0 ? above[Math.min(lastRow + column, above.length - 1)] : field.current ?? undefined;
      }
    }
    next?.focus();
  };

  const grid = (list: readonly string[], label: string) => (
    <div className="emoji-grid" role="group" aria-label={label}>
      {list.map((emoji) => (
        <button key={emoji} className={`emoji-tile${emoji === current ? " on" : ""}`} aria-pressed={emoji === current}
          aria-label={labels.get(emoji) ?? emoji} title={labels.get(emoji)} onClick={() => choose(emoji)}>
          {emoji}
        </button>
      ))}
    </div>
  );

  return (
    <Popover anchor={anchor} onClose={onClose} label={current ? t("Change Icon") : t("Choose Icon")} className="emoji-popover">
      <div ref={panel} onKeyDown={move}>
        <label className="picker-field emoji-search">
          <Icon name="search" />
          <input ref={field} value={query} placeholder={t("Search Emoji")} aria-label={t("Search Emoji")} autoComplete="off" spellCheck={false}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && results[0]) { event.preventDefault(); choose(results[0]); }
              if (event.key === "ArrowDown") {
                event.preventDefault();
                panel.current?.querySelector<HTMLButtonElement>(".emoji-tile")?.focus();
              }
            }} />
        </label>
        {searching ? (
          results.length > 0 ? (
            <>
              <p className="emoji-label">{t("Results")}</p>
              {grid(results, t("Results"))}
            </>
          ) : entries ? <p className="emoji-empty">{t("No emoji found.")}</p> : null
        ) : (
          <>
            {suggested.length > 0 && (
              <>
                <p className="emoji-label">{name ? t("Suggested for “{name}”", { name }) : t("Suggested")}</p>
                {grid(suggested, t("Suggested"))}
              </>
            )}
            <p className="emoji-label">{t("Often Used")}</p>
            {grid(OFTEN_USED_AREA_EMOJI, t("Often Used"))}
          </>
        )}
      </div>
    </Popover>
  );
}
